import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { Box, Tooltip } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { LuX } from 'react-icons/lu'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import PieChart from '@/components/PieChart'
import { useAppSelector } from '@/hooks/redux'
import { getInitials } from '@/pages/stainingStudio/utils'
import { REGIONS_BACKGROUND } from '@/pages/stainingStudio/utils/constants'
import { getTooltipPlacement } from '@/utils/helper'

import BrandValue from './BrandValue'
import LabelValuePair from './LabelValuePair'

const lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = React.memo(
  ({ output, setTooltipOpen }: { output: any; setTooltipOpen: () => void }) => {
    const [expanded, setExpanded] = useState(false)
    const recipe = output?.stainingrecipe?.[0]
    const targetcolor = output?.provided_input?.targetcolor

    return (
      <Box
        display="flex"
        flexGrow={1}
        flexDirection="column"
        gap={1}
        alignItems="flex"
      >
        {expanded && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <LabelWithTooltip
                titleSx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                tooltipSx={{ height: '16px', width: '16px' }}
                label="Staining Target in LAB"
                tooltipText="Apply the staining recipe on the framework and measure before baking it with Optishade to verify the staining target in LAB values. The Chroma ( a*+b*) should be very close to the target. The Luminosity will be higher before baking it because it will drop after firing it."
              />
              <Icon icon={LuX} size={18} onClick={setTooltipOpen} />
              <CloseFullscreenIcon
                onClick={() => setExpanded((prev) => !prev)}
                sx={{ height: 16, width: 16, cursor: 'pointer' }}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabelValuePair label="L:" value={targetcolor?.L} />
              <LabelValuePair label="a:" value={targetcolor?.a} />
              <LabelValuePair label="b:" value={targetcolor?.b} />
            </Box>
          </>
        )}

        {output?.provided_input?.gingivastainingbrand && (
          <>
            {expanded && (
              <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
            )}
            <BrandValue
              value={
                output.provided_input.gingivastainingbrand?.toUpperCase() || ''
              }
              expanded={expanded}
              setExpanded={setExpanded}
              setTooltipOpen={setTooltipOpen}
            />
          </>
        )}

        {Array.isArray(recipe?.ratios) && recipe.ratios.length > 0 && (
          <>
            <Box
              sx={{
                borderTop: '1px solid #999',
                width: '100%',
              }}
            />
            <PieChart
              labels={recipe.component_names}
              values={recipe.ratios}
              height={'78px'}
              width={'78px'}
            />
          </>
        )}
      </Box>
    )
  },
)

const GingivaStainingTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    onDelete: (key: string) => void
  }) => {
    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)

    const prevOutputRef = useRef<any>(null)

    useEffect(() => {
      const currentX = data.position?.x
      const currentY = data.position?.y

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [
      open,
      keyName,
      data.position?.x,
      data.position?.y,
      output,
      canvasImage?.id,
    ])
    return (
      <HtmlKonvaElement key={keyName} x={data.position?.x} y={data.position?.y}>
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={
                output ? (
                  <TooltipContent
                    output={output}
                    setTooltipOpen={() => close(keyName)}
                  />
                ) : null
              }
              arrow
              placement={placement}
              slotProps={{
                popper: {
                  disablePortal: true,
                },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '10px',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(33, 33, 33, 0.80)',
                  },
                },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: REGIONS_BACKGROUND[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: REGIONS_BACKGROUND[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(data.output[keyName])}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

export default GingivaStainingTooltip
